import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from 'config/axiosInstance';
import ClientInfo from './ClientInfo';
import TaskAssignment from './TaskAssignment';
import DriverAssignment from './DriverAssignment';
import { Task } from '../../../reusable/Client/dialog/AddTaskDialog';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AddClient: React.FC = () => {
  const { t } = useTranslation('client');
  const [clientName, setClientName] = useState<string>('');
  const [clientDescription, setClientDescription] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [drivers, setDrivers] = useState<any[]>([]);
  const [selectedDrivers, setSelectedDrivers] = useState<number[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const navigate = useNavigate();
  const [showErrorTooltip, setShowErrorTooltip] = useState(false);

  // Fetch drivers when search query changes
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const response = await axiosInstance.get(
          `/v1/drivers/?search=${searchQuery}`
        );
        const driverData = response.data.results || response.data;
        setDrivers(Array.isArray(driverData) ? driverData : []);
      } catch (error) {
        console.error('Failed to fetch drivers', error);
        setDrivers([]);
      }
    };

    fetchDrivers();
  }, [searchQuery]);

  useEffect(() => {
    // Add an event listener to close the tooltip when clicking outside
    document.addEventListener('click', () => setShowErrorTooltip(false));

    // Cleanup the event listener when the component unmounts
    return () => {
      document.removeEventListener('click', () => setShowErrorTooltip(false));
    };
  }, [showErrorTooltip]);

  // Handle form submission to create a new client
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (selectedTasks.length === 0) {
      setShowErrorTooltip(true);
      return;
    }
    try {
      const newClient = {
        name: clientName,
        description: clientDescription,
        assign_drivers: selectedDrivers,
        tasks: selectedTasks.map((task) => task.id),
      };

      await axiosInstance.post(`/v1/client_deliveries/`, newClient);
      navigate('/client');
    } catch (error) {
      console.error('Failed to add client', error);
    }
  };

  return (
    <section className="bg-white rounded-md m-4">
      <form onSubmit={handleSubmit}>
        <ClientInfo
          clientName={clientName}
          setClientName={setClientName}
          clientDescription={clientDescription}
          setClientDescription={setClientDescription}
        />

        <hr />

        <TaskAssignment
          selectedTasks={selectedTasks}
          setSelectedTasks={setSelectedTasks}
          showErrorTooltip={showErrorTooltip}
          setShowErrorTooltip={() => setShowErrorTooltip(false)}
        />

        <hr />

        <DriverAssignment
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          drivers={drivers}
          selectedDrivers={selectedDrivers}
          handleDriverSelect={(driver) =>
            setSelectedDrivers((prev) => [...prev, driver.id])
          }
          handleDriverRemove={(driverId) =>
            setSelectedDrivers((prev) => prev.filter((id) => id !== driverId))
          }
        />
        <div className="flex justify-end mt-4 gap-4 border-t p-4">
          <button
            type="button"
            className="bg-gray-200 font-semi-bold text-subtitle-2 text-primary/300 h-12 py-2 px-20 rounded-md"
            onClick={() => navigate('/client')}
          >
            {t('action.cancel')}
          </button>
          <button
            type="submit"
            className="bg-primary/300 font-medium text-subtitle-2 text-white h-12 py-2 px-12 rounded-md"
          >
            {t('action.create_client')}
          </button>
        </div>
      </form>
    </section>
  );
};

export default AddClient;
